import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'default' | 'danger' | 'success';
  loading?: boolean;
  loadingMessage?: string;
  /** Override default z-index (10060/10070) when rendered inside higher-z modals */
  zIndex?: number;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  loading = false,
  loadingMessage = 'Processing...',
  zIndex,
}) => {
  const backdropZ = zIndex ?? 10060;
  const containerZ = zIndex ? zIndex + 10 : 10070;
  if (!open) return null;

  const isDanger = variant === 'danger';
  const isSuccess = variant === 'success';

  const iconBg = isDanger
    ? 'linear-gradient(135deg, #fee2e2, #fecaca)'
    : isSuccess
      ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)'
      : 'linear-gradient(135deg, #dbeafe, #bfdbfe)';
  const iconColor = isDanger ? '#dc2626' : isSuccess ? '#16a34a' : '#2563eb';
  const iconBorder = isDanger ? '#fca5a5' : isSuccess ? '#86efac' : '#93c5fd';

  const Icon = isDanger ? IconSystem.alert : isSuccess ? IconSystem.checkCircle : IconSystem.info;

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: backdropZ }} />
      <div className="modal-container modal-sm" style={{ zIndex: containerZ }}>
        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--modal-loading-overlay)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 'inherit',
            gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              border: '3px solid var(--border-color)',
              borderTopColor: isDanger ? '#dc2626' : '#2563eb',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {loadingMessage}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Title Bar — compact */}
        <div className="modal-header" style={{ padding: '0.75rem 1rem', minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
            <Icon size={16} style={{ color: iconColor }} />
            {title}
          </h2>
          <button className="modal-close" onClick={onCancel || onConfirm} disabled={loading} style={{ width: '1.625rem', height: '1.625rem' }}>
            <IconSystem.x size={16} />
          </button>
        </div>

        <div className="modal-content" style={{ textAlign: 'center', padding: '1rem 1.5rem' }}>
          {/* Icon */}
          <div style={{
            width: '3.25rem',
            height: '3.25rem',
            borderRadius: '50%',
            background: iconBg,
            border: `2px solid ${iconBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            boxShadow: `0 4px 12px ${isDanger ? 'rgba(220, 38, 38, 0.12)' : isSuccess ? 'rgba(22, 163, 74, 0.12)' : 'rgba(37, 99, 235, 0.12)'}`,
          }}>
            <Icon size={22} style={{ color: iconColor }} />
          </div>

          {/* Message */}
          <p style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            margin: 0,
          }}>
            {message}
          </p>
        </div>

        {/* Footer — compact */}
        <div className="modal-footer" style={{ justifyContent: 'center', gap: '0.75rem', padding: '0.75rem 1.5rem', minHeight: 'auto' }}>
          {!isSuccess && onCancel && (
            <button className="btn btn-secondary" onClick={onCancel} disabled={loading} style={{ minWidth: '100px', height: '2.125rem', fontSize: '0.8rem' }}>
              {cancelLabel}
            </button>
          )}
          <button
            className={isDanger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={loading}
            style={{ minWidth: '100px', height: '2.125rem', fontSize: '0.8rem' }}
          >
            {isDanger ? <IconSystem.trash size={14} /> : <IconSystem.check size={14} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;
